import { ref } from "vue";
import apiClient from "@/services/apiClient";

/**
 * Composable for managing nearby contacts lookup
 * Isolated from main contacts store to avoid data pollution
 */
export function useNearbyContacts() {
  const contacts = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const dialogOpen = ref(false);

  /**
   * Fetch nearby contacts for a specific account
   * @param {number|string} accountId - The account ID
   */
  const fetchNearbyContacts = async (accountId) => {
    if (!accountId) {
      error.value = "Account ID is required";
      return;
    }

    loading.value = true;
    error.value = null;
    contacts.value = [];

    try {
      const response = await apiClient.get(
        `/api/v1/accounts/${accountId}/nearby_contacts`,
      );

      console.log("Full response:", response);

      // apiClient returns data directly on response object, not in response.data
      if (response && Array.isArray(response.contacts)) {
        console.log("Setting contacts:", response.contacts);
        contacts.value = response.contacts;
      } else if (response && response.message) {
        // Handle case where account has no location
        error.value = response.message;
      } else {
        console.warn("Unexpected response format. Full response:", response);
        contacts.value = [];
      }
    } catch (err) {
      console.error("Error fetching nearby contacts:", err);
      error.value =
        err.response?.data?.message || "Failed to fetch nearby contacts";
      contacts.value = [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * Open the nearby contacts dialog and fetch contacts
   * @param {number|string} accountId - The account ID
   */
  const openDialog = async (accountId) => {
    dialogOpen.value = true;
    await fetchNearbyContacts(accountId);
  };

  /**
   * Close the nearby contacts dialog
   */
  const closeDialog = () => {
    dialogOpen.value = false;
    // Optionally clear data when closing
    // contacts.value = [];
    // error.value = null;
  };

  /**
   * Clear all data
   */
  const reset = () => {
    contacts.value = [];
    error.value = null;
    loading.value = false;
    dialogOpen.value = false;
  };

  return {
    contacts,
    loading,
    error,
    dialogOpen,
    fetchNearbyContacts,
    openDialog,
    closeDialog,
    reset,
  };
}
